import React, { useMemo, useRef, useState } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import { FiSettings } from "react-icons/fi";
import FilterPanel from "../../components/FilterPanel";
import BotaoExportacao from "../../components/Exportacao";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import {
  BuscaInput,
  FilterField,
  FilterGrid,
  FilterLabel,
  FiltroSelect,
} from "../../components/PainelTabela/styles";
import { useDados } from "../../context/DadosContext";
import { useLogGargalos } from "../../hooks/useLogGargalos";
import { useParametrosTOC } from "../../hooks/useParametrosTOC";
import { useTOC } from "../../hooks/useTOC";
import { isNo } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";
import AccountingBridge from "./components/AccountingBridge";
import AdvancedDBRPanel from "./components/AdvancedDBRPanel";
import AdvancedIndicators from "./components/AdvancedIndicators";
import BannerMudancaGargalo from "./components/BannerMudancaGargalo";
import BottleneckCard from "./components/BottleneckCard";
import BottleneckHistoryChart from "./components/BottleneckHistoryChart";
import BufferStatusCard from "./components/BufferStatusCard";
import CriticalAmbientsTable from "./components/CriticalAmbientsTable";
import ModeToggle from "./components/ModeToggle";
import ParametrosDrawer from "./components/ParametrosDrawer";
import PlaybookPanel from "./components/PlaybookPanel";
import {
  FullWidthSection,
  IconButton,
  SectionH2,
  SectionHeader,
  SimplifiedGrid,
} from "./components/styles";
import ThroughputCard from "./components/ThroughputCard";
import WipTotalCard from "./components/WipTotalCard";
import {
  PageState,
  Secao,
  TopBar,
  TopBarActions,
  Titulo,
  Wrapper,
} from "./styles";

const INITIAL_FILTERS = {
  busca: "",
  vendedor: "TODOS",
  tipo: "TODOS",
};

function getUniqueOptions(values) {
  return [...new Set(values.filter(Boolean).map((value) => String(value).trim()))].sort(
    (left, right) =>
      left.localeCompare(right, "pt-BR", { numeric: true, sensitivity: "base" })
  );
}

const PDF_OPCOES = [
  { id: "gargalo", label: "Gargalo do dia", selector: "[data-pdf='gargalo']" },
  { id: "status", label: "Buffers / WIP / Throughput", selector: "[data-pdf='status']" },
  { id: "criticos", label: "Top 10 ambientes críticos", selector: "[data-pdf='criticos']" },
  { id: "playbook", label: "Playbook do gargalo", selector: "[data-pdf='playbook']" },
  { id: "indicadores", label: "Indicadores TOC do mês (avançado)", selector: "[data-pdf='indicadores']" },
  { id: "dbr", label: "DBR · Tambor · Pulmão · Corda (avançado)", selector: "[data-pdf='dbr']" },
  { id: "contabil", label: "Bridge contábil (avançado)", selector: "[data-pdf='contabil']" },
  { id: "historico", label: "Histórico de gargalos (avançado)", selector: "[data-pdf='historico']" },
];

export default function KpisPPCP() {
  const { dados, erro, initialLoading } = useDados();
  const {
    parametros,
    atualizar,
    atualizarOE,
    atualizarZonas,
    definirModo,
    definirCcr,
    resetar,
  } = useParametrosTOC();
  const pageRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [parametrosOpen, setParametrosOpen] = useState(false);
  const [filters, setFilters] = useState({ ...INITIAL_FILTERS });

  const ativos = useMemo(
    () => dados.filter((row) => isNo(row[F.OBRA_FUTURA])),
    [dados]
  );

  const vendedorOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(ativos.map((row) => row[F.VENDEDOR]))],
    [ativos]
  );
  const tipoOptions = useMemo(
    () => ["TODOS", ...getUniqueOptions(ativos.map((row) => getTipoAmbienteSeguro(row)))],
    [ativos]
  );

  const filteredAtivos = useMemo(
    () =>
      ativos.filter((row) => {
        const tipo = getTipoAmbienteSeguro(row);
        const query = filters.busca.trim().toLowerCase();

        if (
          query &&
          ![row[F.NOME_DO_CLIENTE], row[F.AMBIENTE], row[F.VENDEDOR], tipo]
            .join(" ")
            .toLowerCase()
            .includes(query)
        ) {
          return false;
        }
        if (filters.vendedor !== "TODOS" && row[F.VENDEDOR] !== filters.vendedor) {
          return false;
        }
        if (filters.tipo !== "TODOS" && tipo !== filters.tipo) {
          return false;
        }
        return true;
      }),
    [ativos, filters]
  );

  const activeFilterChips = useMemo(() => {
    const chips = [];
    if (filters.busca.trim()) chips.push(`Busca: ${filters.busca.trim()}`);
    if (filters.vendedor !== "TODOS") chips.push(`Vendedor: ${filters.vendedor}`);
    if (filters.tipo !== "TODOS") chips.push(`Tipo: ${filters.tipo}`);
    return chips;
  }, [filters]);

  const toc = useTOC(filteredAtivos, parametros);
  const {
    log: logGargalos,
    ultimaMudanca,
    bannerVisivel,
    marcarBannerLido,
  } = useLogGargalos(toc.snapshotGargalo);

  const exportSnapshotPayload = useMemo(() => {
    if (!toc) return null;
    return {
      titulo: "KPIs PPCP · TOC",
      resumo: [
        { label: "Etapa gargalo",  value: toc.gargalo?.label || "—", type: "text" },
        { label: "WIP total",      value: toc.totalWip,            type: "number" },
        { label: "Throughput mês", value: toc.throughput?.qtdEntregue, type: "number" },
        { label: "Ganho mês (R$)", value: toc.throughput?.ganho,    type: "currency" },
      ],
      secoes: [
        {
          id: "criticos",
          label: "Top 10 ambientes críticos",
          columns: [
            { key: "cliente",   label: "Cliente",   type: "text" },
            { key: "ambiente",  label: "Ambiente",  type: "text" },
            { key: "etapa",     label: "Etapa",     type: "text" },
            { key: "zona",      label: "Zona",      type: "text" },
            { key: "diasNaEtapa", label: "Dias na etapa", type: "days" },
            { key: "valor",     label: "Valor",     type: "currency" },
          ],
          rows: toc.criticos || [],
        },
      ],
      visual: {
        ref: pageRef,
        sections: PDF_OPCOES.map((o) => ({ id: o.id, label: o.label, selector: o.selector })),
      },
    };
  }, [toc]);
  useExportSnapshot("kpis-ppcp", exportSnapshotPayload, pageRef);

  if (initialLoading) {
    return <PageLoader scope="kpis" />;
  }
  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }
  if (!ativos.length) {
    return <PageState>Sem dados disponíveis.</PageState>;
  }

  const isAvancado = parametros.modoVisualizacao === "avancado";

  return (
    <Wrapper ref={pageRef}>
      <TopBar>
        <Titulo>KPIs PPCP · TOC</Titulo>
        <TopBarActions>
          <ModeToggle
            modo={parametros.modoVisualizacao}
            onChange={definirModo}
          />
          <BuscaInput
            type="search"
            aria-label="Busca geral nos KPIs PPCP"
            value={filters.busca}
            onChange={(event) =>
              setFilters((current) => ({ ...current, busca: event.target.value }))
            }
            placeholder="Cliente, ambiente, vendedor..."
          />
          <FilterPanel
            compact
            title="Filtros de KPIs PPCP"
            meta={`${filteredAtivos.length} de ${ativos.length} ambientes ativos considerados`}
            open={filtersOpen}
            onToggle={() => setFiltersOpen((current) => !current)}
            onClose={() => setFiltersOpen(false)}
            onReset={() => setFilters({ ...INITIAL_FILTERS })}
            chips={activeFilterChips}
          >
            <FilterGrid>
              <FilterField>
                <FilterLabel>Vendedor</FilterLabel>
                <FiltroSelect
                  value={filters.vendedor}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      vendedor: event.target.value,
                    }))
                  }
                >
                  {vendedorOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
              <FilterField>
                <FilterLabel>Tipo de ambiente</FilterLabel>
                <FiltroSelect
                  value={filters.tipo}
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      tipo: event.target.value,
                    }))
                  }
                >
                  {tipoOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === "TODOS" ? "Todos" : option}
                    </option>
                  ))}
                </FiltroSelect>
              </FilterField>
            </FilterGrid>
          </FilterPanel>
          <BotaoExportacao
            titulo="KPIs PPCP · TOC"
            resumo={[
              { label: "Etapa gargalo",  value: toc.gargalo?.label || "—", type: "text" },
              { label: "WIP total",      value: toc.totalWip,            type: "number" },
              { label: "Throughput mês", value: toc.throughput?.qtdEntregue, type: "number" },
              { label: "Ganho mês (R$)", value: toc.throughput?.ganho,    type: "currency" },
            ]}
            secoes={[
              {
                id: "criticos",
                label: "Top 10 ambientes críticos",
                columns: [
                  { key: "cliente",   label: "Cliente",   type: "text" },
                  { key: "ambiente",  label: "Ambiente",  type: "text" },
                  { key: "etapa",     label: "Etapa",     type: "text" },
                  { key: "zona",      label: "Zona",      type: "text" },
                  { key: "diasNaEtapa", label: "Dias na etapa", type: "days" },
                  { key: "valor",     label: "Valor",     type: "currency" },
                ],
                rows: toc.criticos || [],
              },
            ]}
            visual={{
              ref: pageRef,
              sections: PDF_OPCOES.map((o) => ({ id: o.id, label: o.label, selector: o.selector })),
            }}
          />
          <IconButton
            type="button"
            aria-label="Abrir parâmetros TOC"
            title="Parâmetros TOC"
            onClick={() => setParametrosOpen(true)}
          >
            <FiSettings size={16} />
          </IconButton>
        </TopBarActions>
      </TopBar>

      <BannerMudancaGargalo
        visivel={bannerVisivel}
        mudanca={ultimaMudanca}
        onDispensar={marcarBannerLido}
      />

      <FullWidthSection data-pdf="gargalo">
        <BottleneckCard
          gargalo={toc.gargalo}
          janelaDiasUteis={toc.janelaDiasUteis}
        />
      </FullWidthSection>

      <Secao data-pdf="status">
        <SimplifiedGrid>
          <BufferStatusCard contagemPorZona={toc.contagemPorZona} />
          <WipTotalCard
            totalWip={toc.totalWip}
            wipPorEtapa={toc.wipPorEtapa}
          />
          <ThroughputCard
            throughput={toc.throughput}
            burndown={toc.burndown}
            metaAmbientes={parametros.metaAmbientesMes}
          />
        </SimplifiedGrid>
      </Secao>

      <Secao data-pdf="criticos">
        <SectionHeader>
          <SectionH2>Top 10 ambientes críticos</SectionH2>
        </SectionHeader>
        <CriticalAmbientsTable criticos={toc.criticos} />
      </Secao>

      <Secao data-pdf="playbook">
        <PlaybookPanel
          etapa={toc.playbookDoGargalo?.etapa || null}
          playbook={toc.playbookDoGargalo?.playbook || null}
        />
      </Secao>

      {isAvancado && (
        <>
          <Secao data-pdf="indicadores">
            <SectionHeader>
              <SectionH2>Indicadores TOC do mês</SectionH2>
            </SectionHeader>
            <AdvancedIndicators
              contabil={toc.contabil}
              throughput={toc.throughput}
              historicoT={toc.historicoT}
            />
          </Secao>

          <Secao data-pdf="dbr">
            <SectionHeader>
              <SectionH2>DBR · Tambor · Pulmão · Corda</SectionH2>
            </SectionHeader>
            <AdvancedDBRPanel
              tambor={toc.tambor}
              ccrEtapa={toc.ccrEtapa}
              pulmaoHist={toc.pulmaoHist}
              proximasLiberacoes={toc.proximasLiberacoes}
              tamborDias={toc.tamborDias}
            />
          </Secao>

          <Secao data-pdf="contabil">
            <SectionHeader>
              <SectionH2>Bridge contábil · TOC ↔ DRE / Balanço</SectionH2>
            </SectionHeader>
            <AccountingBridge contabil={toc.contabil} />
          </Secao>

          <Secao data-pdf="historico">
            <BottleneckHistoryChart log={logGargalos} hoje={toc.hoje} />
          </Secao>
        </>
      )}

      <ParametrosDrawer
        open={parametrosOpen}
        onClose={() => setParametrosOpen(false)}
        parametros={parametros}
        atualizar={atualizar}
        atualizarOE={atualizarOE}
        atualizarZonas={atualizarZonas}
        definirCcr={definirCcr}
        resetar={resetar}
      />
    </Wrapper>
  );
}
